/**
 * React Query mutation hooks for fields.
 * Handles create, update, and delete operations.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { mixpanel } from "common/analytics";
import { queryKeys } from "common/queryKeys";
import API from "common/utils/Api";
import {
  prepareFieldRequest,
  processFieldResponse,
} from "common/utils/helpers";
import { Farm } from "model/farmTypes";
import { Field, FieldResponse } from "model/fieldTypes";
import { clearTilesFromCache } from "services/fieldOperations";

const fieldApi = new API<FieldResponse>("fields");

/**
 * React Query mutation hook for creating a field
 */
export function useCreateField() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (field: Field) => {
      const res = await fieldApi.createOne(prepareFieldRequest(field));
      const newField = await fieldApi.getOne(res.guid);
      const processed = processFieldResponse([newField]);
      return processed[0];
    },
    onSuccess: async (newField) => {
      // Add new field to cache
      queryClient.setQueryData<Field>(
        queryKeys.fields.detail(newField.guid),
        newField
      );

      // Update parent farm's fields array in React Query cache
      if (newField.farm) {
        const farm = queryClient.getQueryData<Farm>(
          queryKeys.farms.detail(newField.farm)
        );
        if (farm && !farm.fields?.includes(newField.guid)) {
          queryClient.setQueryData<Farm>(
            queryKeys.farms.detail(newField.farm),
            {
              ...farm,
              fields: [...(farm.fields || []), newField.guid],
            }
          );
        }
      }

      await queryClient.invalidateQueries({ queryKey: queryKeys.fields.all });
    },
  });
}

/**
 * React Query mutation hook for updating a field
 */
export function useUpdateField() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (field: Field) => {
      await fieldApi.updateOne(prepareFieldRequest(field));
      const updatedField = await fieldApi.getOne(field.guid);
      const processed = processFieldResponse([updatedField]);
      return processed[0];
    },
    onMutate: async (field) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: queryKeys.fields.detail(field.guid),
      });

      // Snapshot previous value
      const previousField = queryClient.getQueryData<Field>(
        queryKeys.fields.detail(field.guid)
      );

      // Optimistically update the geometry
      if (previousField) {
        queryClient.setQueryData<Field>(queryKeys.fields.detail(field.guid), {
          ...previousField,
          geom: field.geom,
        });
      }

      return { previousField };
    },
    onError: (_err, field, context) => {
      // Rollback on error
      if (context?.previousField) {
        queryClient.setQueryData(
          queryKeys.fields.detail(field.guid),
          context.previousField
        );
      }
    },
    onSuccess: async (updatedField) => {
      // Invalidate and refetch
      await queryClient.invalidateQueries({
        queryKey: queryKeys.fields.detail(updatedField.guid),
      });
      await queryClient.invalidateQueries({ queryKey: queryKeys.fields.all });
      mixpanel.track("Field validated", { guid: updatedField.guid });
    },
  });
}

/**
 * React Query mutation hook for deleting a field
 */
export function useDeleteField() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (field: Field) => {
      const res = await fieldApi.deleteOne(field.guid);
      if (!res.ok) {
        throw new Error(`Failed to delete field: ${res.status}`);
      }
      clearTilesFromCache(field.guid);
      return { fieldGuid: field.guid, farmGuid: field.farm };
    },
    onSuccess: async ({ fieldGuid, farmGuid }) => {
      // Remove from cache
      queryClient.removeQueries({
        queryKey: queryKeys.fields.detail(fieldGuid),
      });

      // Update parent farm's fields array in React Query cache
      if (farmGuid) {
        const farm = queryClient.getQueryData<Farm>(
          queryKeys.farms.detail(farmGuid)
        );
        if (farm) {
          queryClient.setQueryData<Farm>(queryKeys.farms.detail(farmGuid), {
            ...farm,
            fields: (farm.fields || []).filter((id) => id !== fieldGuid),
          });
        }
      }

      await queryClient.invalidateQueries({ queryKey: queryKeys.fields.all });
    },
  });
}
